import React, { useEffect, useState } from "react";
import { toCamelCase } from "@/Common/helpers/toCamelCase";
import { IMappedPosition } from "@/Common/types/positions/api";
import { IDefaultPayPlans } from "@/Settings/types/Common/model";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { Box, Grid, Stack } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useServices } from "@/PayPlans/hooks/useServices/useServices";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PayPlanSelectorProps, TCamelCaseModality } from "@/Settings/types/SystemSettings/defaultPayPlans";
import ViewPayPlanSelected from "./ViewPayPlanSelected";
import { errorLogger } from "@/Common/helpers/logger";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import InfoSubTitle from "../../../common/InfoSubTitle";

const resolvePositionType = (managedPosition?: IMappedPosition[]) => (managedPosition?.[0]?.isDepartmental ? "Departmental" : "Producer");

const resolvePayPlans = (payPlansArray: unknown) => (Array.isArray(payPlansArray) ? payPlansArray : []);

const payPlanCallback = ({
	result,
	onUpdateAvailablePayPlan,
	queryClient,
	elementId,
	locationId,
}: {
	result: { data: { [key: string]: { _id: string; available: boolean } } };
	onUpdateAvailablePayPlan: { getter: string; query: string };
	queryClient: QueryClient;
	elementId: string;
	locationId: string;
}) => {
	const getter = onUpdateAvailablePayPlan?.getter;
	const response = getter ? result?.data?.[getter] : { _id: "", available: false };
	if (response) {
		setQueryData({ queryClient, elementId, locationId }, { payPlan: response });
	}
};

const setQueryData = (
	{ queryClient, elementId, locationId }: { queryClient: QueryClient; elementId: string; locationId: string },
	args: { payPlan: { _id: string; available: boolean } }
) => {
	const { payPlan } = args;
	queryClient.setQueriesData({ queryKey: ["PayPlansList", elementId, locationId] }, (cachedData) => {
		return (cachedData as { _id: string; available: boolean; name: string; position: string }[])?.map((item) => {
			if (item._id === payPlan._id) {
				return { ...item, available: payPlan.available };
			}
			return item;
		});
	});
};

const DefaultPayPlans = React.memo(
	(props: {
		locationId: string;
		position: string;
		elementId: string;
		mappedPositions?: IMappedPosition[];
		defaultPayPlans?: IDefaultPayPlans[];
	}) => {
		const { locationId, position, elementId, defaultPayPlans = [], mappedPositions = [] } = props;
		const queryClient = useQueryClient();
		const managedPosition = mappedPositions?.filter((item) => item?.systemPosition.startsWith(position));
		const isNewAndPreownedPosition = managedPosition.length > 1;
		const positionType = resolvePositionType(managedPosition);
		const { queries, subscriptions } = useServices(position, positionType);
		const { addSubscription, getSubscription } = useSubscriptions();
		const onUpdateAvailablePayPlan = subscriptions?.get("onUpdateAvailablePayPlan") as { getter: string; query: string };
		const selectedPayPlan = defaultPayPlans?.find((element) => element.position === position);

		const { data: payPlansArray = [], isLoading: isLoadingPayPlans } = useQuery({
			queryKey: ["PayPlansList", elementId, locationId],
			queryFn: async () => {
				const result = (await queries.getPayPlans(locationId, position)) ?? [];
				return result.map((item: { available: boolean; locked: boolean; name: string; position: string; _id: string }) => ({
					available: item.available,
					locked: item.locked,
					name: item.name,
					position: item.position,
					_id: item._id,
				}));
			},
			enabled: !!locationId,
			staleTime: Infinity,
		});

		const { mutate, isPending } = useMutation({
			mutationFn: (input: { position: string; defaultPayPlan: string; newVehiclePayPlan: string; preOwnedVehiclePayPlan: string }) =>
				updateSystemSettings(locationId, { defaultPayPlans: input }, "DEFAULT_PAY_PLANS"),
			onError: (error) => errorLogger(error),
		});

		const payPlans = resolvePayPlans(payPlansArray);

		//Subscriptions
		for (const payPlan of payPlans) {
			const updateAvailablePayPlanSubscription = getSubscription(["onUpdateAvailablePayPlan", payPlan._id]);
			updateAvailablePayPlanSubscription?.subscriptions?.onUpdateAvailablePayPlan?.setCallback(
				(result: { data: { [key: string]: { _id: string; available: boolean } } }) =>
					payPlanCallback({ result, onUpdateAvailablePayPlan, queryClient, elementId, locationId })
			);
		}

		const handleChangeClick = (name: string, value: string) => {
			const updatedSettings = {
				defaultPayPlan: "",
				newVehiclePayPlan: "",
				position: props.position,
				preOwnedVehiclePayPlan: "",
				...(selectedPayPlan ?? {}),
				[name]: value,
			};
			mutate(updatedSettings);
		};

		const getSelectedPlayPlanId = (selectedItem = "") => {
			const payPlanId = payPlans?.find((payPlan: { name: string }) => payPlan.name === selectedItem);
			return String(payPlanId?._id);
		};

		useEffect(() => {
			if (payPlans?.length && addSubscription) {
				for (const payPlan of payPlans) {
					addSubscription(
						["onUpdateAvailablePayPlan", payPlan._id],
						[
							{
								context: "onUpdateAvailablePayPlan",
								query: onUpdateAvailablePayPlan?.query,
								variables: {
									locationId,
									_id: payPlan._id,
								},
							},
						]
					);
				}
			}
		}, [payPlans, addSubscription]);

		return (
			<Grid container item>
				<Grid item container>
					{isNewAndPreownedPosition ? (
						<>
							<Grid container spacing={0} alignItems="center" justifyContent="flex-start" className="!mt-[-16px] !mb-[-16px]">
								<Grid item xs={11.57} sx={{ width: "100%" }}>
									<PayPlanSelector
										modality={"New Vehicle Pay Plan"}
										handleChangeClick={handleChangeClick}
										payPlans={payPlans}
										selectedPayPlan={selectedPayPlan}
										isLoading={isLoadingPayPlans}
										getSelectedPlayPlanId={getSelectedPlayPlanId}
										position={position}
										disabled={isPending}
									/>
								</Grid>
								<Grid item xs={0.4} sx={{ marginLeft: "-710px" }}>
									<InfoSubTitle title={"Overtime"} className="!mt-[10px] !mb-[6px]" />
									<OvertimeSwitch
										modality={"New Vehicle Pay Plan"}
										managedPosition={managedPosition}
										isNewAndPreownedPosition={isNewAndPreownedPosition}
										position={position}
										locationId={locationId}
									/>
								</Grid>
							</Grid>
							<Grid container spacing={0} alignItems="center" justifyContent="flex-start" className="!mt-[-16px] !mb-[-16px]">
								<Grid item xs={11.57} sx={{ width: "100%" }}>
									<PayPlanSelector
										modality={"Pre-Owned Vehicle Pay Plan"}
										handleChangeClick={handleChangeClick}
										payPlans={payPlans}
										selectedPayPlan={selectedPayPlan}
										isLoading={isLoadingPayPlans}
										getSelectedPlayPlanId={getSelectedPlayPlanId}
										position={position}
										disabled={isPending}
									/>
								</Grid>
								<Grid item xs={0.4} sx={{ marginLeft: "-710px" }}>
									<InfoSubTitle title={"Overtime"} className="!mt-[10px] !mb-[6px]" />
									<OvertimeSwitch
										modality={"Pre-Owned Vehicle Pay Plan"}
										managedPosition={managedPosition}
										isNewAndPreownedPosition={isNewAndPreownedPosition}
										position={position}
										locationId={locationId}
									/>
								</Grid>
							</Grid>
						</>
					) : (
						<Grid container spacing={0} alignItems="center" justifyContent="flex-start" className="!mt-[-16px] !mb-[-16px]">
							<Grid item xs={11.57} sx={{ width: "100%" }}>
								<PayPlanSelector
									modality={"Default Pay Plan"}
									handleChangeClick={handleChangeClick}
									payPlans={payPlans}
									selectedPayPlan={selectedPayPlan}
									isLoading={isLoadingPayPlans}
									getSelectedPlayPlanId={getSelectedPlayPlanId}
									position={position}
									disabled={isPending}
								/>
							</Grid>
							<Grid item xs={0.4} sx={{ marginLeft: "-710px" }}>
								<InfoSubTitle title={"Overtime"} className="!mt-[10px] !mb-[6px]" />
								<OvertimeSwitch
									modality={"Default Pay Plan"}
									managedPosition={managedPosition}
									isNewAndPreownedPosition={isNewAndPreownedPosition}
									position={position}
									locationId={locationId}
								/>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Grid>
		);
	}
);

const OvertimeSwitch = ({
	modality,
	managedPosition,
	isNewAndPreownedPosition,
	position,
	locationId,
}: {
	modality: string;
	managedPosition: IMappedPosition[];
	isNewAndPreownedPosition: boolean;
	position: string;
	locationId: string;
}) => {
	const firstWordInModality = modality.split(" ")[0].toLowerCase();
	const mappedPosition = managedPosition?.find((item) => item.systemPosition.toLowerCase().includes(firstWordInModality));
	const getOvertimeValue = isNewAndPreownedPosition ? mappedPosition?.overtime : managedPosition?.[0]?.overtime ?? false;
	const [overtimeEnabled, setOvertimeEnabled] = useState(getOvertimeValue);

	const handleOvertimeChange = (value: boolean) => {
		if (isUpdatingMappedPositions) {
			return;
		}

		setOvertimeEnabled(value);

		const searchString =
			firstWordInModality !== "default" ? `${position.toLowerCase()} ${firstWordInModality}` : position.toLowerCase();

		const updatedItem = managedPosition?.find((item) => item.systemPosition.toLowerCase().includes(searchString));
		if (updatedItem) {
			updateMappedPositions({ ...updatedItem, overtime: value });
		}
	};

	const { mutate: updateMappedPositions, isPending: isUpdatingMappedPositions } = useMutation({
		mutationFn: (input: IMappedPosition) => updateSystemSettings(locationId, { mappedPositions: input }, "MAPPED_POSITIONS"),
		onError: (error) => errorLogger(error),
	});

	useEffect(() => {
		setOvertimeEnabled(getOvertimeValue);
	}, [getOvertimeValue]);

	return (
		<CustomizedSwitches
			key={"overtimeEnabled"}
			textRight={true}
			textChecked={"Enable Position Overtime"}
			textUnChecked={"Enable Position Overtime"}
			checked={overtimeEnabled}
			onChange={(event) => handleOvertimeChange(event)}
		/>
	);
};

const PayPlanSelector = ({
	payPlans,
	selectedPayPlan,
	modality,
	handleChangeClick,
	isLoading,
	position,
	getSelectedPlayPlanId,
	disabled,
}: PayPlanSelectorProps) => {
	const camelCaseModality = toCamelCase(modality) as TCamelCaseModality;
	const payPlanSelected = payPlans?.find((payPlan) => payPlan.name === selectedPayPlan?.[camelCaseModality]);
	const filteredPayPlans = payPlans.filter((payPlan) => payPlan.available || payPlan.locked);
	const [finalSelectedPayPlan, setFinalSelectedPayPlan] = useState<{ name: string } | null>(null);

	const stringSelectedPayPlan = JSON.stringify(selectedPayPlan);
	useEffect(() => {
		setFinalSelectedPayPlan(selectedPayPlan?.[camelCaseModality] ? { name: selectedPayPlan?.[camelCaseModality] } : null);
		return () => {
			setFinalSelectedPayPlan(null);
		};
	}, [stringSelectedPayPlan]);

	const handleChange = (selected: { name: string } | null) => {
		setFinalSelectedPayPlan(selected);
		if (selected) {
			handleChangeClick(camelCaseModality, selected.name);
		} else {
			handleChangeClick(camelCaseModality, "");
		}
	};

	return (
		<Box sx={{ marginTop: "38px" }}>
			<Grid item xs={4} pr={4}>
				<InfoSubTitle title={modality} className="!mt-[16px] !mb-[6px]" />
				<Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="flex-end">
					<SmartSelector
						showBy="name"
						selectBy="name"
						options={filteredPayPlans}
						selected={finalSelectedPayPlan}
						inputProps={{
							placeholder: "Select a Pay Plan",
						}}
						disabled={disabled}
						loading={isLoading}
						sx={{ minWidth: "99%" }}
						onChangeAction={(value) => handleChange(value as { name: string })}
						clearable
					/>
					<ViewPayPlanSelected
						selectedPlan={getSelectedPlayPlanId(selectedPayPlan?.[camelCaseModality])}
						// TODO: remove the check for Sales Associate when the other positions views are created
						disabled={!(position === "Sales Associate" && selectedPayPlan?.[camelCaseModality])}
					/>
				</Stack>
				<Box sx={{ height: "24px", display: "flex", alignItems: "center" }}>
					{payPlanSelected && !payPlanSelected.available && (
						<Grid container direction={"row"}>
							<Grid>
								<WarningAmberRoundedIcon className="text-warning w-[20px] pl-[2px] pb-[6px]" />
							</Grid>
							<Grid className="font-sans text-warning text-[14.5px] pl-[3px]">Pay plan is currently unavailable.</Grid>
						</Grid>
					)}
				</Box>
			</Grid>
		</Box>
	);
};

export default DefaultPayPlans;
